import { formatNumber } from "@ciq/shared/utils/format"

export const END_ZONE_BUILDING_FILL_COLOR = "#EEE"
export const DEFAULT_METRIC_FILL_COLOR = "#AAA"
export const DEFAULT_METRIC_OUTLINE_COLOR = "#888"

class UtilizationRange {
  constructor(min, max, title, color, options) {
    const modifier = options?.modifier != null ? options.modifier : 1
    const symbol = options?.symbol != null ? options.symbol : ""
    const symbolHasSpace = options?.symbolHasSpace != null ? options.symbolHasSpace : true

    this.id = `utilization-range-${min * modifier}-${max * modifier}`
    this.min = min
    this.max = max
    this.title = title

    let range = ""
    if (min != null && max != null) {
      range += `${formatNumber(Math.round(min * modifier))} - ${formatNumber(Math.round(max * modifier))}`
    } else if (min != null) {
      range += `${formatNumber(Math.round(min * modifier))} - ~`
    } else {
      range += `~ - ${formatNumber(Math.round(max * modifier))}${symbol}`
    }

    if (symbol) {
      range += symbolHasSpace ? ` ${symbol}` : symbol
    }

    this.range = range
    this.color = color
  }

  isInRange(value) {
    if (value == null) {
      return false
    }

    if (value < this.min) {
      return false
    }

    return this.max != null ? this.max >= value : true
  }
}

const DefaultRangeData = [
  {
    label: "Low Usage",
    color: "#bfbfeb",
    minWeight: 0,
    maxWeight: 0.2,
  },
  {
    label: "Moderate Usage",
    color: "#988cd3",
    minWeight: 0.2,
    maxWeight: 0.5,
  },
  {
    label: "Active Usage",
    color: "#6f5cb4",
    minWeight: 0.5,
    maxWeight: 0.8,
  },
  {
    label: "High Usage",
    color: "#43367E",
    minWeight: 0.8,
    maxWeight: 1,
  },
]

const convertToUtilizationRange = (maxValue, options) => {
  return data => {
    return new UtilizationRange(
      maxValue * data.minWeight,
      maxValue * data.maxWeight,
      data.label,
      data.color,
      options
    )
  }
}

const PercentileRange = [
  new UtilizationRange(0, 0.02, "Inactive", "#e2f6ff", {
    modifier: 100,
    symbol: "%",
    symbolHasSpace: false,
  }),
  new UtilizationRange(0.02, 0.2, "Low Usage", "#bfbfeb", {
    modifier: 100,
    symbol: "%",
    symbolHasSpace: false,
  }),
  new UtilizationRange(0.2, 0.5, "Moderate Usage", "#988cd3", {
    modifier: 100,
    symbol: "%",
    symbolHasSpace: false,
  }),
  new UtilizationRange(0.5, 0.8, "Active Usage", "#6f5cb4", {
    modifier: 100,
    symbol: "%",
    symbolHasSpace: false,
  }),
  new UtilizationRange(0.8, 1, "High Usage", "#43367E", {
    modifier: 100,
    symbol: "%",
    symbolHasSpace: false,
  }),
]

export const buildColorRanges = (column, maxValue) => {
  if (column == null || maxValue == null) {
    return null
  }

  if (column.isPercentageMetric()) {
    return PercentileRange
  }

  return DefaultRangeData.map(
    convertToUtilizationRange(maxValue, { symbol: column.symbol, symbolHasSpace: false })
  )
}

export const getColor = (ranges, value) => {
  if (ranges == null || value == null) {
    return DEFAULT_METRIC_FILL_COLOR
  }

  for (const range of ranges) {
    if (range.isInRange(value)) {
      return range.color
    }
  }

  if (ranges.length > 0) {
    const firstRange = ranges[0]
    if (value < firstRange.min) {
      return firstRange.color
    }

    return ranges[ranges.length - 1].color
  }

  return DEFAULT_METRIC_FILL_COLOR
}
